import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Train, Clock, MapPin, Filter, Loader2, AlertCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { searchTrain } from '../../../../../services/train-tracking/trainService';
import TrainTrackingResults from './TrainTrackingResults';
import TableSkeleton from '../../../../../components/feedback/TableSkeleton';
import EmptyStateTable from '../../../../../components/feedback/EmptyStateTable';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import { getCachedVouchers, peekCachedVouchers } from '../../../../../services/vouchers/vouchersCache';
import {
  trainColumns,
  trainGhostRows,
  parseAnyDate,
  getTimelineBadge,
  formatBookingId
} from '../../../utils/trackingHelpers';
import { DAYS_FILTER_OPTIONS } from '../../../../../data/tracking/trackingData';

const UpcomingTrainsTable = () => {
  const navigate = useNavigate();
  const [trainVouchers, setTrainVouchers] = useState([]);
  const [loading, setLoading] = useState(!peekCachedVouchers());
  const [daysFilter, setDaysFilter] = useState(3);
  const [expandedRow, setExpandedRow] = useState(null);
  const [rowDetailData, setRowDetailData] = useState({});
  const [fetchingRow, setFetchingRow] = useState(null);
  const [errorRow, setErrorRow] = useState(null);

  useEffect(() => {
    const processVouchers = (vouchers) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filterDateLimit = new Date();
      filterDateLimit.setDate(today.getDate() + daysFilter);
      filterDateLimit.setHours(23, 59, 59, 999);

      const upcomingTrains = [];

      (vouchers || []).forEach((voucher) => {
        const trainBookings =
          voucher.travel_bookings?.filter(
            (tb) => tb.mode_of_transport?.toLowerCase() === 'train' || tb.mode_of_transport === 'Train'
          ) || [];

        if (trainBookings.length === 0) return;

        const startDateStr = voucher.tour_info?.start_date;

        trainBookings.forEach((train) => {
          let dateToUse = train.journey_date;
          if (!dateToUse || dateToUse === 'N/A') {
            dateToUse = startDateStr;
          }

          if (dateToUse && dateToUse !== 'N/A') {
            const start = parseAnyDate(dateToUse);

            if (start && start >= today && start <= filterDateLimit) {
              const rawVehicleNum =
                train.vehicle_number || train.flight_train_number || train.train_number || '';
              const extractedNum = rawVehicleNum.split('/')[0]?.replace(/[^\d]/g, '') || rawVehicleNum;
              const trainName =
                train.operator ||
                train.train_name ||
                (rawVehicleNum.includes('/') ? rawVehicleNum.split('/')[1]?.trim() : '') ||
                'Express';

              upcomingTrains.push({
                guestName: voucher.guest_information?.guest_name || 'N/A',
                bookingId: voucher.booking_id,
                startDate: startDateStr || 'N/A',
                trainNumber: extractedNum || rawVehicleNum || 'N/A',
                trainName: trainName,
                pnr: train.pnr || train.pnr_number || 'N/A',
                from: train.from || train.from_city || 'N/A',
                to: train.to || train.to_city || 'N/A',
                departureTime: train.departure_time || 'N/A',
                journeyDate: dateToUse,
                fullVoucher: voucher
              });
            }
          }
        });
      });

      setTrainVouchers(upcomingTrains);
    };

    const loadVouchers = async () => {
      const cached = peekCachedVouchers();
      if (cached) {
        processVouchers(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const vouchers = await getCachedVouchers();
        processVouchers(vouchers);
      } catch (err) {
        console.error('Failed to fetch vouchers for trains', err);
      } finally {
        setLoading(false);
      }
    };

    loadVouchers();
  }, [daysFilter]);

  const handleRowClick = async (train, rowKey) => {
    if (expandedRow === rowKey) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(rowKey);

    if (rowDetailData[rowKey]) return;

    try {
      setFetchingRow(rowKey);
      setErrorRow(null);

      let cleanTrainNum = String(train.trainNumber).replace(/[^0-9]/g, '');
      if (!cleanTrainNum) {
        setErrorRow(rowKey);
        setFetchingRow(null);
        return;
      }

      let journeyDate = new Date().toISOString().split('T')[0];
      if (train.journeyDate && train.journeyDate !== 'N/A') {
        const parsed = parseAnyDate(train.journeyDate);
        if (parsed) {
          journeyDate = parsed.toISOString().split('T')[0];
        }
      }

      const result = await searchTrain(cleanTrainNum, journeyDate);

      if (result && result.data) {
        setRowDetailData((prev) => ({
          ...prev,
          [rowKey]: result.data
        }));
      } else {
        setErrorRow(rowKey);
      }
    } catch (err) {
      console.error('Error fetching live train for row:', err);
      setErrorRow(rowKey);
    } finally {
      setFetchingRow(null);
    }
  };

  const calculateDaysToGo = (dateStr) => {
    const badge = getTimelineBadge(dateStr);
    return badge.text || 'Scheduled';
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 overflow-hidden relative mt-8">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 relative z-30 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-44">
            <CustomSelect
              value={daysFilter}
              onChange={(val) => setDaysFilter(Number(val))}
              options={DAYS_FILTER_OPTIONS}
              icon={Filter}
              buttonClassName="!py-1.5 !px-3 !text-xs font-bold border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-xl !h-[36px]"
            />
          </div>

          <div className="hidden sm:block h-4 w-px bg-slate-200"></div>

          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
              <Train size={14} />
            </div>
            Upcoming Train Trips
          </h3>
        </div>
      </div>

      <div className="overflow-x-auto relative z-0 scrollbar-hide">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="train-skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="py-1"
            >
              <TableSkeleton columns={trainColumns} rows={4} cardWrapper={false} />
            </motion.div>
          ) : (
            <motion.div
              key="train-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {trainVouchers.length > 0 ? (
                <table className="w-full text-left border-separate border-spacing-y-0 min-w-[700px]">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      <th className="pb-4 pl-4">Guest & ID</th>
                      <th className="pb-2">Train Details</th>
                      <th className="pb-2">Route & Time</th>
                      <th className="pb-2 text-right pr-4">Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainVouchers.map((train, idx) => (
                      <tr
                        key={idx}
                        onClick={() => handleRowClick(train, `${train.bookingId}-${idx}`)}
                        className="group cursor-pointer transition-all duration-300 hover:bg-slate-50 border-b border-slate-100 last:border-none"
                      >
                        <td className="py-4 px-4 rounded-l-2xl border-y border-l border-slate-100 group-hover:border-primary/10 transition-colors">
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-800 text-sm flex items-center gap-2">
                              {train.guestName}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">
                              {formatBookingId(train.bookingId)}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 border-y border-slate-100 group-hover:border-primary/10 transition-colors">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                              <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-md">
                                #{train.trainNumber}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono tracking-wider">
                              PNR: {train.pnr}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 border-y border-slate-100 group-hover:border-primary/10 transition-colors">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1 text-xs text-slate-700 font-semibold">
                              <MapPin size={10} className="text-primary/60" />
                              <span>
                                {train.from} → {train.to}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-slate-500">
                              <Clock size={10} />
                              <span>{train.departureTime}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 rounded-r-2xl border-y border-r border-slate-100 group-hover:border-primary/10 transition-colors text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs font-bold text-slate-700">
                              {train.journeyDate !== 'N/A' ? train.journeyDate : train.startDate}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                                calculateDaysToGo(train.journeyDate !== 'N/A' ? train.journeyDate : train.startDate) === 'Today'
                                  ? 'bg-red-50 text-red-600 border-red-100'
                                  : calculateDaysToGo(train.journeyDate !== 'N/A' ? train.journeyDate : train.startDate) === 'Tomorrow'
                                  ? 'bg-amber-50 text-amber-600 border-amber-100'
                                  : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              }`}
                            >
                              {calculateDaysToGo(train.journeyDate !== 'N/A' ? train.journeyDate : train.startDate)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyStateTable
                  title="No upcoming train trips"
                  description={`When your vouchers include train bookings with train numbers or PNRs, live train running status, platform details, and schedule timelines for the next ${daysFilter} days will appear here automatically.`}
                  columns={trainColumns}
                  rows={trainGhostRows}
                  primaryAction={{
                    label: 'Create Voucher',
                    onClick: () => navigate('/create-voucher')
                  }}
                  renderRow={(row) => (
                    <div className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                      <div className="col-span-3">
                        <p className="text-slate-800 text-sm font-bold">{row.guest}</p>
                        <p className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">{row.bookingId}</p>
                      </div>
                      <div className="col-span-3 flex flex-col gap-1">
                        <span className="bg-[#067978]/10 text-[#067978] px-2 py-0.5 rounded-md text-xs font-bold w-fit">
                          #{row.trainNumber} · {row.trainName}
                        </span>
                      </div>
                      <div className="col-span-3 flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 text-xs text-slate-700 font-semibold">
                          <MapPin size={10} className="text-[#067978]/60" />
                          <span>
                            {row.from} → {row.to}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                          <Clock size={10} />
                          <span>{row.departureTime}</span>
                        </div>
                      </div>
                      <div className="col-span-3 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${row.statusClass}`}>
                          {row.timeline}
                        </span>
                      </div>
                    </div>
                  )}
                  renderMobileRow={(row) => (
                    <div className={`bg-white border rounded-2xl p-3.5 border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-slate-800 text-sm font-bold">{row.guest}</h4>
                          <p className="text-[11px] text-slate-400 font-normal">
                            Train #{row.trainNumber} · {row.from} → {row.to}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${row.statusClass}`}>
                          {row.timeline}
                        </span>
                      </div>
                    </div>
                  )}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Train Detail Modal */}
      {expandedRow && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div
            className="bg-white rounded-[32px] shadow-2xl border border-slate-100 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 slide-in-from-bottom-10 duration-500"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-2xl text-primary shadow-sm">
                  <Train size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 leading-tight">Live Train Status</h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    #{trainVouchers.find((t) => `${t.bookingId}-${trainVouchers.indexOf(t)}` === expandedRow)?.trainNumber} •{' '}
                    {trainVouchers.find((t) => `${t.bookingId}-${trainVouchers.indexOf(t)}` === expandedRow)?.guestName}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setExpandedRow(null)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
              <div className="bg-slate-50/50 rounded-3xl p-4 border border-slate-100">
                {fetchingRow === expandedRow ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 size={40} className="text-primary animate-spin" />
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">
                      Scanning track status...
                    </p>
                  </div>
                ) : errorRow === expandedRow ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-4 text-slate-400 text-center">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-400">
                      <AlertCircle size={32} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">Connection Failed</p>
                      <p className="text-[10px] font-bold uppercase tracking-widest mt-1">
                        Unable to fetch live status for this train
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const train = trainVouchers.find(
                          (t) => `${t.bookingId}-${trainVouchers.indexOf(t)}` === expandedRow
                        );
                        handleRowClick(train, expandedRow);
                      }}
                      className="px-6 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
                    >
                      Retry Fetch
                    </button>
                  </div>
                ) : rowDetailData[expandedRow] ? (
                  <TrainTrackingResults
                    trainData={rowDetailData[expandedRow]}
                    trainNumber={
                      trainVouchers.find((t) => `${t.bookingId}-${trainVouchers.indexOf(t)}` === expandedRow)?.trainNumber
                    }
                  />
                ) : null}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-50 bg-slate-50/30 flex justify-end">
              <button
                onClick={() => setExpandedRow(null)}
                className="px-8 py-3 bg-white border-2 border-slate-100 text-slate-600 rounded-2xl font-bold text-xs hover:bg-slate-50 hover:border-slate-200 transition-all active:scale-95 shadow-sm cursor-pointer"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingTrainsTable;
